import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetRevenueByEmployeeReportQuery } from 'store/api/reportingFinance';
import { RevenueByEmployeeRow, RevenueByEmployeeTotals } from 'types/reporting/finance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import {
    employeeLabel,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    GREEN_OR_RED,
} from '../reportCells';
import {
    employeeFilter,
    isActiveFilter,
    jobRoleFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    searchSelectors,
} from '../reportFilters';
import { inBaseCurrency, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = RevenueByEmployeeRow;
type Totals = RevenueByEmployeeTotals;

const REPORT_KEY = 'revenue-by-employee';

function buildFilterConfig(): FilterConfig {
    const filters = [
        periodFilter(),
        employeeFilter('employeeId', 'reporting.filter.employee'),
        isActiveFilter(),
        jobRoleFilter(),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'last-month' }),
        quickFilters: quickFiltersFrom(filters, { isActive: 'reporting.filter.is-active' }),
        searchBarSelectors: searchSelectors('employeeName', 'jobRole'),
        filters,
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'employeeName',
            accessorKey: 'employeeName',
            header: t('reporting.column.employee'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.employeeId}>
                    {employeeLabel(row.original)}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'jobRole',
            accessorKey: 'jobRole',
            header: t('reporting.column.job-role'),
            meta: { widthWeight: 1, minPx: 140 },
        },
        {
            id: 'invoicedHours',
            accessorKey: 'invoicedHours',
            header: t('reporting.column.invoiced-hours'),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.8,
                minPx: 130,
                textAlign: 'right',
                renderTotal: (totals) => <ReportNumberCell value={totals?.invoicedHours} />,
            },
            cell: tonedCell(rows, (row) => row.invoicedHours, GREEN_OR_RED),
        },
        {
            id: 'invoicedAmount',
            accessorKey: 'invoicedAmount',
            header: inBaseCurrency(t('reporting.column.invoiced-amount'), currency),
            meta: {
                ...TONED_COLUMN_META,
                widthWeight: 0.9,
                minPx: 150,
                textAlign: 'right',
                renderTotal: (totals) => <ReportNumberCell value={totals?.invoicedAmount} />,
            },
            cell: tonedCell(rows, (row) => row.invoicedAmount, GREEN_OR_RED),
        },
    ];
}

export function RevenueByEmployeeReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetRevenueByEmployeeReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.employees"
                singularTextId="reporting.employee"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
